import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ActivityIndicator, useWindowDimensions } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import MarkdownText from '../components/MarkdownText';
import InfoTooltip from '../components/InfoTooltip';
import Modal from '../components/Modal';
import {
    FaFire, FaChartLine, FaCommentAlt, FaMagic, FaChevronDown, FaChevronUp,
    FaShieldAlt, FaBrain, FaBullhorn, FaUserSecret, FaShareAlt, FaArrowLeft,
    FaGlobe, FaRss, FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaUsers,
    FaNewspaper, FaExternalLinkAlt, FaReply, FaEye, FaBinoculars, FaFacebookF, FaSearch, FaTv,
} from 'react-icons/fa';
import ReactionModal from '../components/ReactionModal';
import ShareModal from '../components/ShareModal';
import CounterNarrativeModal from '../components/CounterNarrativeModal';
import PercentageLoader from '../components/PercentageLoader';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';

// Module-level theme shorthands — mutated inside the component on every render
let T = {};
let THEME = {}; // kept for sub-components (CounterNarrativeModal etc.) that still read THEME

const PLATFORM_ICONS = {
    twitter:        FaTwitter,
    telegram:       FaTelegram,
    tiktok:         FaTiktok,
    instagram:      FaInstagram,
    facebook_group: FaFacebookF,
    rss:            FaRss,
    webz:           FaNewspaper,
    news:           FaNewspaper,
    tv:             FaTv,
};

const PLATFORM_COLORS = {
    twitter:        '#1da1f2',
    telegram:       '#0088cc',
    tiktok:         '#ff0050',
    instagram:      '#e1306c',
    facebook_group: '#1877F2',
    rss:            '#22c55e',
    webz:           '#10b981',
    news:           '#8e8e93',
    tv:             '#9f1239',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getHeatColor(velocity) {
    const val = parseFloat(velocity) || 0;
    if (val >= 5.0) return T.primary;
    return T.textMuted;
}

function formatVelocity(vel) { return (vel || 0).toFixed(1) + 'x'; }

function formatTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(isoString) {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function NarrativeListItem({ narrative, isSelected, onSelect, isTrending }) {
    const { t } = useTranslation();
    return (
        <div
            className={`nm-item${isSelected ? ' sel' : ''}`}
            onClick={onSelect}
            style={{
                borderRadius: 10,
                border: `1px solid ${isSelected ? '#0288d1' : T.border}`,
                marginBottom: 4,
                padding: '14px 12px',
                background: isSelected ? '#f0f9ff' : T.card,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{
                    fontSize: 11, fontWeight: 500,
                    padding: '3px 10px', borderRadius: 20,
                    border: `1px solid ${T.border}`,
                    background: T.surface, color: T.textSec,
                }}>
                    {formatVelocity(narrative.velocity_1h)}
                </span>
                <span style={{ color: T.textMuted, fontSize: 10, marginLeft: 'auto' }}>
                    {timeAgo(narrative.last_seen_at)}
                </span>
            </div>
            <div style={{
                color: T.text, fontSize: 13, fontWeight: 700, lineHeight: '18px',
                marginBottom: narrative.summary ? 6 : 6,
            }}>
                {narrative.title}
            </div>
            {narrative.summary && (
                <div style={{
                    color: T.textSec, fontSize: 11, lineHeight: '16px',
                    marginBottom: 8,
                }}>
                    {narrative.summary}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FaCommentAlt size={9} color={T.textMuted} />
                <span style={{ color: T.textMuted, fontSize: 10 }}>{narrative.message_count}</span>
            </div>
        </div>
    );
}

function StrategyListItem({ strategy, isSelected, onSelect }) {
    const { t } = useTranslation();
    return (
        <div
            className={`nm-item${isSelected ? ' sel' : ''}`}
            onClick={onSelect}
            style={{
                borderRadius: 10,
                border: `1px solid ${isSelected ? '#0288d1' : T.border}`,
                marginBottom: 4,
                padding: '10px 12px',
                background: isSelected ? '#f0f9ff' : T.card,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <span style={{
                    fontSize: 11, fontWeight: 500,
                    color: T.primary, background: `${T.primary}10`,
                    padding: '3px 10px', borderRadius: 20,
                }}>
                    {t('app.trends.strategy_badge')}
                </span>
            </div>
            <div style={{
                color: T.text, fontSize: 12, fontWeight: 600, lineHeight: '16px',
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                marginBottom: 4,
            }}>
                {strategy.strategy_name}
            </div>
            <div style={{
                color: T.textMuted, fontSize: 10, lineHeight: '14px',
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
            }}>
                Re: {strategy.narrative_title || 'Unknown Narrative'}
            </div>
        </div>
    );
}

function SectionHeader({ icon: Icon, iconColor, title, isCollapsed, onToggle, extra }) {
    return (
        <div
            className="nm-section-header"
            onClick={onToggle}
            style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                padding: '12px 0',
                borderBottom: `1px solid ${T.border}`,
                marginBottom: isCollapsed ? 0 : 16,
            }}
        >
            <div style={{
                width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                background: '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon size={11} color="#6b7280" />
            </div>
            <span style={{
                color: T.textMuted, fontSize: 11, fontWeight: 700,
                letterSpacing: 0.5, flex: 1,
            }}>
                {title}
            </span>
            {extra}
            {isCollapsed
                ? <FaChevronDown size={10} color={T.textMuted} />
                : <FaChevronUp size={10} color={T.textMuted} />}
        </div>
    );
}

// ── Main Screen ───────────────────────────────────────────────────────────────

const TrendsScreen = () => {
    const { width } = useWindowDimensions();
    const { theme } = useTheme();
    const { t, i18n } = useTranslation();
    const isMobile = width < 768;

    // Mutate module-level theme objects on every render
    const isDark = theme.bg === '#050507' || (theme.bg && theme.bg.startsWith('#0'));
    T = isDark ? {
        bg:       '#0f1117',
        card:     '#1a1d27',
        surface:  '#22252f',
        border:   '#2e3140',
        text:     '#f1f5f9',
        textSec:  '#94a3b8',
        textMuted:'#64748b',
        primary:  '#38bdf8',
        success:  '#4ade80',
        warning:  '#fbbf24',
        error:    '#f87171',
        purple:   '#a78bfa',
    } : {
        bg:       '#ffffff',
        card:     '#ffffff',
        surface:  '#f9fafb',
        border:   '#e5e7eb',
        text:     '#111827',
        textSec:  '#374151',
        textMuted:'#9ca3af',
        primary:  '#0288d1',
        success:  '#16a34a',
        warning:  '#d97706',
        error:    '#dc2626',
        purple:   '#7c3aed',
    };
    Object.assign(THEME, theme);

    const { activeOrg, mode } = useOrg();
    const activeOrgId = mode === 'all' ? 'all' : (activeOrg?.id || null);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pendingNarrativeId = useRef(location.state?.highlightNarrativeId || searchParams.get('narrative') || null);

    // ── Data state ──
    const [activeNarratives, setActiveNarratives] = useState([]);
    const [trendingNarratives, setTrendingNarratives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // ── Selection / detail state ──
    const [selectedNarrative, setSelectedNarrative] = useState(null);
    const [narrativeMessages, setNarrativeMessages] = useState([]);
    const [narrativeStrategies, setNarrativeStrategies] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [enrichmentData, setEnrichmentData] = useState(null);
    const [loadingEnrichment, setLoadingEnrichment] = useState(false);
    const [enrichmentRequested, setEnrichmentRequested] = useState(false);
    const [selectedStrategies, setSelectedStrategies] = useState([]);

    const [narrativeTrackState, setNarrativeTrackState] = useState('idle');

    // ── UI state ──
    const [detailTab, setDetailTab] = useState('analysis');
    const [collapsed, setCollapsed] = useState({
        strategy: false, analysis: false,
        summary: false, keyVoices: false, themes: true, emerging: true,
        signals: true, sources: true, entities: true,
    });
    const [showDetail, setShowDetail] = useState(false); // mobile only

    // ── View mode ──
    const [viewMode, setViewMode] = useState('trends');
    const [savedStrategies, setSavedStrategies] = useState([]);
    const [loadingStrategies, setLoadingStrategies] = useState(false);
    const [selectedStrategyId, setSelectedStrategyId] = useState(null);

    // ── Advisor recommendation ──
    const [advisorRec, setAdvisorRec] = useState(null);
    const [loadingAdvisorRec, setLoadingAdvisorRec] = useState(false);

    // ── Reaction / Counter-Narrative ──
    const [showReactionModal, setShowReactionModal] = useState(false);
    const [reactionNarrative, setReactionNarrative] = useState(null);
    const [reactionLoading, setReactionLoading] = useState(false);
    const [reactionError, setReactionError] = useState(null);
    const [counterNarrative, setCounterNarrative] = useState(null);

    // ── Influencer modal ──
    const [selectedInfluencer, setSelectedInfluencer] = useState(null);
    const [showInfluencerModal, setShowInfluencerModal] = useState(false);

    // ── Share modal ──
    const [shareNarrative, setShareNarrative] = useState(null);

    // ── Column widths (draggable dividers) ──
    const [sidebarW, setSidebarW] = useState(340);
    const [midW, setMidW] = useState(600);
    const dragRef = useRef(null); // which divider: 'left' | 'right'
    const dragStartRef = useRef(null); // { x, sidebarW, midW }

    const startDrag = (divider, e) => {
        e.preventDefault();
        dragRef.current = divider;
        dragStartRef.current = { x: e.clientX, sidebarW, midW };
        const onMove = (mv) => {
            const rawDx = mv.clientX - dragStartRef.current.x;
            if (dragRef.current === 'left') {
                const dx = i18n.language === 'he' ? -rawDx : rawDx;
                setSidebarW(Math.max(200, Math.min(500, dragStartRef.current.sidebarW + dx)));
            } else {
                const dx = i18n.language === 'he' ? -rawDx : rawDx;
                setMidW(Math.max(160, Math.min(600, dragStartRef.current.midW + dx)));
            }
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const toggleSection = (key) =>
        setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

    // ── Data fetching ────────────────────────────────────────────────────────

    const fetchData = useCallback(async () => {
        try {
            const [active, trending] = await Promise.all([
                ListsManager.fetchActiveNarratives(50, 0, 'velocity_1h', activeOrgId),
                ListsManager.fetchTrendingNarratives(10, activeOrgId),
            ]);
            setActiveNarratives((active || []).filter(n => n.title));
            setTrendingNarratives((trending || []).filter(n => n.title));
        } catch (error) {
            console.error('Error fetching trends:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeOrgId]);

    const fetchStrategies = async () => {
        setLoadingStrategies(true);
        try {
            const strategies = await ListsManager.fetchAllStrategies();
            setSavedStrategies(strategies || []);
        } catch (error) {
            console.error('Error fetching strategies:', error);
        } finally {
            setLoadingStrategies(false);
        }
    };

    useEffect(() => { fetchData(); AnalyticsService.logActivity('view_trends'); }, [fetchData]);

    useEffect(() => {
        if (!pendingNarrativeId.current || loading) return;
        const targetId = pendingNarrativeId.current;
        pendingNarrativeId.current = null;
        const match = activeNarratives.find(n => n.id === targetId) || trendingNarratives.find(n => n.id === targetId);
        if (match) {
            openNarrativeDetails(match);
        } else if (targetId) {
            openNarrativeDetails({ id: targetId, title: '' });
        }
        window.history.replaceState({}, '');
    }, [loading, activeNarratives, trendingNarratives]);

    useEffect(() => {
        if (viewMode === 'strategies') fetchStrategies();
    }, [viewMode]);


    // ── Detail loading ───────────────────────────────────────────────────────

    const openNarrativeDetails = async (narrative) => {
        setSelectedNarrative(narrative);
        setNarrativeTrackState('idle');
        setDetailTab('analysis');
        setCollapsed({ strategy: false, analysis: false, summary: false, keyVoices: false, signals: true, sources: true, entities: true });
        setLoadingMessages(true);
        setLoadingEnrichment(false);
        setEnrichmentRequested(false);
        setNarrativeMessages([]);
        setNarrativeStrategies([]);
        setEnrichmentData(null);
        setSelectedStrategies([]);
        setAdvisorRec(null);
        setLoadingAdvisorRec(true);
        if (isMobile) setShowDetail(true);

        // Stage 1: fast calls — posts (direct DB, ~100-300ms)
        try {
            const details = await ListsManager.fetchNarrativeDetails(narrative.id);
            if (details?.messages) setNarrativeMessages(details.messages);
        } catch (error) {
            console.error('Failed to fetch narrative details:', error);
        } finally {
            setLoadingMessages(false);
        }

        // Stage 2: fire advisor as fire-and-forget
        ListsManager.evaluateNarrativeAction(narrative.id).then(rec => {
            if (rec) setAdvisorRec(rec);
        }).catch(error => {
            console.error('Failed to fetch advisor recommendation:', error);
        }).finally(() => {
            setLoadingAdvisorRec(false);
        });
    };

    const handleStrategyToggle = (strategy) => {
        setSelectedStrategies(prev =>
            prev.includes(strategy) ? prev.filter(s => s !== strategy) : [...prev, strategy]
        );
    };

    const handleRequestEnrichment = async () => {
        if (!selectedNarrative || loadingEnrichment) return;
        setEnrichmentRequested(true);
        setLoadingEnrichment(true);
        try {
            const enrichment = await ListsManager.fetchNarrativeEnrichment(selectedNarrative.id);
            if (enrichment) setEnrichmentData(enrichment);
        } catch (error) {
            console.error('Failed to fetch enrichment:', error);
        } finally {
            setLoadingEnrichment(false);
        }
    };

    const handleReactToNarrative = async (narrative, enrichment, e) => {
        if (e) e.stopPropagation();
        setReactionNarrative(narrative);
        setShowReactionModal(true);
        setReactionLoading(true);
        setReactionError(null);
        setCounterNarrative(null);
        try {
            const result = await ListsManager.generateCounterNarrative(narrative.id, selectedStrategies);
            if (result?.success) setCounterNarrative(result);
            else setReactionError(result?.error_message || 'Failed to generate strategy.');
        } catch (error) {
            setReactionError(error.message || 'Failed to generate strategy');
        } finally {
            setReactionLoading(false);
        }
    };

    const handleInfluencerPress = (influencer) => {
        setSelectedInfluencer(influencer);
        setShowInfluencerModal(true);
    };

    // ── Trending ID set for badge ────────────────────────────────────────────
    const trendingIds = new Set(trendingNarratives.map(n => n.id));

    // ── Loading screen ───────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100vh', background: T.bg,
            }}>
                <PercentageLoader fontSize={48} color="#0a84ff" style={{ marginBottom: 16 }} />
                <span style={{ color: '#0a84ff', fontSize: 10, letterSpacing: 2, fontWeight: 600 }}>
                    {t('app.trends.establishing_connection')}
                </span>
            </div>
        );
    }

    // ── CSS injection ────────────────────────────────────────────────────────
    const css = `
      .nm-item { transition: border-color 0.15s, background 0.15s; cursor: pointer; }
      .nm-item:hover { border-color: #7dd3fc !important; background: #f0f9ff !important; }
      .nm-item.sel { border-color: #0288d1 !important; background: #f0f9ff !important; }
      .nm-section-header { transition: opacity 0.1s; }
      .nm-section-header:hover { opacity: 0.75; }
      .nm-tab { cursor: pointer; transition: color 0.12s, border-color 0.12s; user-select: none; }
      .nm-tab:hover { color: ${T.text} !important; }
      .nm-cta:hover { filter: brightness(1.15); }
      .nm-toggle:hover { background: ${T.surface} !important; }
      .nm-post-tile { transition: border-color 0.15s; }
      .nm-post-tile:hover { border-color: #7dd3fc !important; }
      .nm-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
      .nm-divider:hover, .nm-divider:active { background: ${T.primary}55; }
      .nm-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.15); border-radius: 2px; }
    `;

    // ── Left sidebar content ─────────────────────────────────────────────────

    const renderSidebarList = () => {
        if (viewMode === 'trends') {
            const withVelocity = activeNarratives.filter(n => (n.velocity_1h || 0) > 0 || (n.velocity_24h || 0) > 0);
            const withoutVelocity = activeNarratives.filter(n => !((n.velocity_1h || 0) > 0 || (n.velocity_24h || 0) > 0));
            const allItems = [...withVelocity, ...withoutVelocity];
            return allItems.map(n => (
                <NarrativeListItem
                    key={n.id}
                    narrative={n}
                    isSelected={selectedNarrative?.id === n.id}
                    onSelect={() => openNarrativeDetails(n)}
                    isTrending={trendingIds.has(n.id)}
                />
            ));
        } else {
            if (loadingStrategies) {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                        <ActivityIndicator size="small" color="#0a84ff" />
                    </div>
                );
            }
            if (!savedStrategies.length) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: '40px 16px', gap: 8 }}>
                        <FaMagic size={24} color={T.textMuted} />
                        <span style={{ color: T.textMuted, fontSize: 13, textAlign: 'center' }}>
                            {t('app.trends.no_saved_strategies')}
                        </span>
                    </div>
                );
            }
            return savedStrategies.map(s => (
                <StrategyListItem
                    key={s.id}
                    strategy={s}
                    isSelected={selectedStrategyId === s.id}
                    onSelect={() => {
                        setSelectedStrategyId(s.id);
                        setCounterNarrative(s);
                        setReactionNarrative({
                            id: s.narrative_id,
                            title: s.narrative_title || 'Unknown Narrative',
                            summary: 'Strategy loaded from saved versions.',
                        });
                        setShowReactionModal(true);
                    }}
                />
            ));
        }
    };

    // ── Right panel: enrichment sections ────────────────────────────────────

    const renderStrategySection = () => {
        const SEVERITY_COLORS = {
            critical: '#ff453a', high: '#ff9f0a', medium: '#ffd60a', low: T.textMuted,
        };

        const ACTION_LABELS = {
            counter_narrative:       t('app.trends.label_counter_narrative'),
            react:                   t('app.trends.label_respond'),
            report:                  t('app.trends.label_report'),
            amplify:                 t('app.trends.label_amplify'),
            amplify_ugc:             t('app.trends.label_amplify_ugc'),
            monitor:                 t('app.trends.label_monitor'),
            ignore:                  t('app.trends.label_ignore'),
            issue_apology:           t('app.trends.label_issue_apology'),
            escalate:                t('app.trends.label_escalate'),
            service_recovery:        t('app.trends.label_service_recovery'),
            empathetic_intervention: t('app.trends.label_empathetic_intervention'),
            trend_jack:              t('app.trends.label_trend_jack'),
            launch_micro_campaign:   t('app.trends.label_launch_campaign'),
            banter:                  t('app.trends.label_banter'),
            outreach:                t('app.trends.label_outreach'),
            routine_engagement:      t('app.trends.label_routine_engagement'),
            product_insight:         t('app.trends.label_product_insight'),
        };

        const ACTION_COLORS = {
            counter_narrative:     T.error,
            react:                 T.primary,
            report:                T.error,
            amplify:               T.success,
            amplify_ugc:           T.success,
            monitor:               T.textMuted,
            ignore:                T.textMuted,
            issue_apology:         '#ff9f0a',
            escalate:              T.error,
            service_recovery:      T.primary,
            empathetic_intervention: '#ff9f0a',
            trend_jack:            T.purple,
            launch_micro_campaign: T.primary,
            banter:                '#ffd60a',
            outreach:              T.success,
            routine_engagement:    T.textMuted,
            product_insight:       T.textMuted,
        };

        // Actions that warrant a "draft" CTA → opens CounterNarrativeModal
        const DRAFT_ACTIONS = new Set([
            'counter_narrative', 'react', 'issue_apology', 'trend_jack',
            'launch_micro_campaign', 'banter', 'outreach', 'routine_engagement',
            'empathetic_intervention', 'service_recovery',
        ]);

        // Actions that warrant a Share CTA
        const SHARE_ACTIONS = new Set(['amplify', 'amplify_ugc']);

        const ACTION_BUTTON_META = {
            counter_narrative:     { label: t('app.trends.action_draft_counter_post'),   icon: FaShieldAlt },
            react:                 { label: t('app.trends.action_draft_reply'),           icon: FaReply },
            issue_apology:         { label: t('app.trends.action_draft_apology'),         icon: FaShieldAlt },
            trend_jack:            { label: t('app.trends.action_draft_trend_post'),      icon: FaBullhorn },
            launch_micro_campaign: { label: t('app.trends.action_draft_campaign_post'),   icon: FaBullhorn },
            banter:                { label: t('app.trends.action_draft_banter'),          icon: FaCommentAlt },
            outreach:              { label: t('app.trends.action_draft_outreach'),        icon: FaCommentAlt },
            routine_engagement:    { label: t('app.trends.action_quick_thanks'),          icon: FaCommentAlt },
            empathetic_intervention: { label: t('app.trends.action_draft_response'),      icon: FaCommentAlt },
            service_recovery:      { label: t('app.trends.action_draft_recovery_reply'),  icon: FaCommentAlt },
            amplify:               { label: t('app.trends.action_share'),                 icon: FaShareAlt },
            amplify_ugc:           { label: t('app.trends.action_share_content'),         icon: FaShareAlt },
        };

        const rec = advisorRec;
        const recAction = rec?.recommended_action;
        const showDraftButton = recAction && DRAFT_ACTIONS.has(recAction);
        const showShareButton = recAction && SHARE_ACTIONS.has(recAction);
        const showStrategyOptions = !rec || DRAFT_ACTIONS.has(recAction);

        const actionColor = recAction ? (ACTION_COLORS[recAction] || T.primary) : T.primary;
        const severityColor = rec ? (SEVERITY_COLORS[rec.severity] || T.textMuted) : T.textMuted;
        const buttonMeta = recAction ? ACTION_BUTTON_META[recAction] : null;

        return (
            <div style={{ marginBottom: 8 }}>
                <SectionHeader
                    icon={FaMagic}
                    iconColor={T.primary}
                    title={t('app.trends.response_strategy')}
                    isCollapsed={collapsed.strategy}
                    onToggle={() => toggleSection('strategy')}
                    extra={enrichmentData?.narrative_stage && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.8,
                            color: T.purple, background: `${T.purple}18`,
                            padding: '2px 6px', borderRadius: 4, marginRight: 4,
                        }}>
                            {enrichmentData.narrative_stage.toUpperCase()}
                        </span>
                    )}
                />
                {!collapsed.strategy && (
                    <div style={{ marginBottom: 20 }}>

                        {/* ── Advisor Recommendation Card ── */}
                        {loadingAdvisorRec && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '12px 14px', borderRadius: 10,
                                border: `1px solid ${T.border}`, background: T.surface,
                                marginBottom: 16,
                            }}>
                                <ActivityIndicator size="small" color={T.primary} />
                                <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1 }}>
                                    {t('app.trends.analyzing')}
                                </span>
                            </div>
                        )}

                        {!loadingAdvisorRec && rec && (
                            <div style={{
                                padding: '14px 14px 12px',
                                borderRadius: 10,
                                border: `1px solid ${T.border}`,
                                background: T.surface,
                                marginBottom: 16,
                            }}>
                                {/* Badges row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontSize: 9, fontWeight: 800, letterSpacing: 0.8,
                                        textTransform: 'uppercase',
                                        color: actionColor, background: `${actionColor}20`,
                                        padding: '3px 8px', borderRadius: 5,
                                    }}>
                                        {ACTION_LABELS[recAction] || recAction}
                                    </span>
                                    {rec.severity && (
                                        <span style={{
                                            fontSize: 9, fontWeight: 700, letterSpacing: 0.6,
                                            textTransform: 'uppercase',
                                            color: severityColor, background: `${severityColor}18`,
                                            padding: '3px 8px', borderRadius: 5,
                                        }}>
                                            {rec.severity.toUpperCase()}
                                        </span>
                                    )}
                                </div>

                                {/* Reasoning */}
                                {rec.reasoning && (
                                    <div style={{
                                        color: T.text, fontSize: 12, lineHeight: '17px',
                                        marginBottom: 10, fontStyle: 'italic',
                                    }}>
                                        "{rec.reasoning}"
                                    </div>
                                )}

                                {/* Suggested steps */}
                                {rec.suggested_steps?.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
                                            letterSpacing: 0.8, marginBottom: 6 }}>
                                            {t('app.trends.suggested_steps')}
                                        </div>
                                        {rec.suggested_steps.map((step, i) => (
                                            <div key={i} style={{
                                                display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4,
                                            }}>
                                                <span style={{ color: actionColor, fontSize: 10, fontWeight: 700,
                                                    marginTop: 1, flexShrink: 0 }}>
                                                    {i + 1}.
                                                </span>
                                                <span style={{ color: T.textSec, fontSize: 11, lineHeight: '16px' }}>
                                                    {step}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {showDraftButton && buttonMeta && (
                                        <button
                                            className="nm-cta"
                                            onClick={(e) => handleReactToNarrative(selectedNarrative, enrichmentData, e)}
                                            style={{
                                                flex: 1, padding: '9px 12px',
                                                background: actionColor, border: 'none', borderRadius: 8,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                gap: 6, cursor: 'pointer', minWidth: 120,
                                            }}
                                        >
                                            {buttonMeta.icon && <buttonMeta.icon size={11} color="#fff" />}
                                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>
                                                {buttonMeta.label}
                                            </span>
                                        </button>
                                    )}
                                    {showShareButton && (
                                        <button
                                            className="nm-cta"
                                            onClick={() => setShareNarrative(selectedNarrative)}
                                            style={{
                                                flex: 1, padding: '9px 12px',
                                                background: T.success, border: 'none', borderRadius: 8,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                gap: 6, cursor: 'pointer', minWidth: 120,
                                            }}
                                        >
                                            <FaShareAlt size={11} color="#fff" />
                                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>
                                                {buttonMeta?.label || t('app.trends.action_share')}
                                            </span>
                                        </button>
                                    )}
                                    {!showDraftButton && !showShareButton && recAction && !['monitor', 'ignore'].includes(recAction) && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '8px 12px', borderRadius: 8,
                                            background: `${actionColor}15`,
                                            border: `1px solid ${actionColor}30`,
                                        }}>
                                            <FaEye size={10} color={actionColor} />
                                            <span style={{ color: actionColor, fontSize: 11, fontWeight: 600 }}>
                                                {ACTION_LABELS[recAction] || recAction}
                                            </span>
                                        </div>
                                    )}
                                    {/* Track Narrative button */}
                                    <button
                                        className="nm-cta"
                                        disabled={narrativeTrackState !== 'idle'}
                                        onClick={async () => {
                                            if (!selectedNarrative?.id) return;
                                            setNarrativeTrackState('loading');
                                            try {
                                                const result = await ListsManager.trackNarrative(selectedNarrative.id, activeOrg?.id);
                                                setNarrativeTrackState(result ? 'done' : 'idle');
                                            } catch (e) { console.error(e); setNarrativeTrackState('idle'); }
                                        }}
                                        style={{
                                            padding: '9px 12px',
                                            background: narrativeTrackState === 'done' ? 'rgba(48,209,88,0.12)' : 'transparent',
                                            border: `1px solid ${narrativeTrackState === 'done' ? 'rgba(48,209,88,0.4)' : 'rgba(191,90,242,0.4)'}`,
                                            borderRadius: 8,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            gap: 6, minWidth: 120,
                                            cursor: narrativeTrackState === 'loading' ? 'wait' : narrativeTrackState === 'done' ? 'default' : 'pointer',
                                            opacity: narrativeTrackState === 'loading' ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {narrativeTrackState === 'loading' ? (
                                            <>
                                                <ActivityIndicator size={10} color="#bf5af2" />
                                                <span style={{ color: '#bf5af2', fontWeight: 700, fontSize: 12 }}>{t('app.trends.tracking')}</span>
                                            </>
                                        ) : narrativeTrackState === 'done' ? (
                                            <>
                                                <FaChartLine size={11} color="#30d158" />
                                                <span style={{ color: '#30d158', fontWeight: 700, fontSize: 12 }}>{t('app.trends.tracked')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaBinoculars size={11} color="#bf5af2" />
                                                <span style={{ color: '#bf5af2', fontWeight: 700, fontSize: 12 }}>{t('app.trends.track_narrative')}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Generate button — when no advisor rec */}
                        {!rec && !loadingAdvisorRec && (
                            <button
                                className="nm-cta"
                                onClick={(e) => handleReactToNarrative(selectedNarrative, enrichmentData, e)}
                                style={{
                                    width: '100%', padding: '12px 0',
                                    background: T.primary, border: 'none', borderRadius: 8,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: 8, cursor: 'pointer',
                                }}
                            >
                                <FaMagic size={12} color="#fff" />
                                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                                    {t('app.trends.generate_ai_response')}
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderAnalysisSection = () => {
        const PLATFORM_ICONS = {
            twitter: FaTwitter, telegram: FaTelegram,
            tiktok: FaTiktok, instagram: FaInstagram,
            facebook_group: FaFacebookF,
        };
        const PLATFORM_COLORS = {
            twitter: '#1d9bf0', telegram: '#24A1DE',
            tiktok: '#ff0050', instagram: '#e1306c',
            facebook_group: '#1877F2',
        };
        const STANCE_COLORS = { hostile: T.error, friendly: T.success, neutral: T.textMuted, pro: T.success, anti: T.error };
        const AVATAR_PALETTE = ['#e5e7eb', '#e5e7eb', '#e5e7eb', '#e5e7eb', '#e5e7eb', '#e5e7eb', '#e5e7eb'];

        // Summary content: prefer structured field, fall back to blob
        const summaryContent = enrichmentData.trend_summary_text;
        // Key voices: prefer structured field, fall back to key_influencers from source analysis
        const keyVoices = enrichmentData.trend_key_voices?.length > 0
            ? enrichmentData.trend_key_voices
            : (enrichmentData.key_influencers || []);
        const themes = enrichmentData.trend_main_themes || [];
        const emerging = enrichmentData.trend_emerging;
        const citations = enrichmentData.trend_summary_citations || [];

        const SubHeader = ({ icon: Icon, iconColor, label, count, collapsedKey }) => (
            <div
                className="nm-section-header"
                onClick={() => toggleSection(collapsedKey)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer', padding: '10px 0',
                    borderBottom: `1px solid ${T.border}`,
                    marginBottom: collapsed[collapsedKey] ? 0 : 14,
                }}
            >
                <div style={{
                    width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                    background: `${iconColor}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={9} color={iconColor} />
                </div>
                <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
                    {label}
                    {count != null && (
                        <span style={{ color: T.primary, marginLeft: 6 }}>({count})</span>
                    )}
                </span>
                {collapsed[collapsedKey]
                    ? <FaChevronDown size={9} color={T.textMuted} />
                    : <FaChevronUp size={9} color={T.textMuted} />}
            </div>
        );

        return (
            <div style={{ marginBottom: 8 }}>
                <SectionHeader
                    icon={FaChartLine}
                    iconColor={T.primary}
                    title={t('app.trends.narrative_analysis')}
                    isCollapsed={collapsed.analysis}
                    onToggle={() => toggleSection('analysis')}
                />
                {!collapsed.analysis && (
                    <div style={{ marginBottom: 20, paddingLeft: 4 }}>

                        {/* ── Summary ── */}
                        {summaryContent && (
                            <div style={{ marginBottom: 4 }}>
                                <SubHeader icon={FaNewspaper} iconColor={T.primary} label={t('app.trends.summary')} collapsedKey="summary" />
                                {!collapsed.summary && (
                                    <div style={{ marginBottom: 16 }}>
                                        <MarkdownText content={summaryContent} />
                                        {citations.length > 0 && (() => {
                                            const domainMap = {};
                                            citations.forEach(url => {
                                                try {
                                                    const host = new URL(url).hostname.replace(/^www\./, '');
                                                    if (!domainMap[host]) domainMap[host] = { url, count: 0 };
                                                    domainMap[host].count++;
                                                } catch {}
                                            });
                                            return (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8,
                                                    marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                                                    {Object.entries(domainMap).map(([host, { url, count }]) => (
                                                        <div
                                                            key={host}
                                                            onClick={() => window.open(url, '_blank')}
                                                            style={{
                                                                background: 'rgba(10,132,255,0.08)',
                                                                padding: '5px 10px', borderRadius: 20,
                                                                cursor: 'pointer',
                                                                border: '1px solid rgba(10,132,255,0.2)',
                                                            }}
                                                        >
                                                            <span style={{ fontSize: 11, color: T.primary, fontWeight: 500 }}>
                                                                {host}{count > 1 ? ` (${count})` : ''}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Main Themes ── */}
                        {themes.length > 0 && (
                            <div style={{ marginBottom: 4 }}>
                                <SubHeader icon={FaChartLine} iconColor={T.warning} label={t('app.trends.main_themes')} count={themes.length} collapsedKey="themes" />
                                {!collapsed.themes && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                                        {themes.map((theme, idx) => (
                                            <div key={idx} style={{
                                                background: T.surface,
                                                border: `1px solid ${T.border}`,
                                                borderRadius: 8, padding: '6px 12px',
                                            }}>
                                                <span style={{ color: T.textSec, fontSize: 12, lineHeight: '17px' }}>
                                                    {theme}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Key Voices ── */}
                        {keyVoices.length > 0 && (
                            <div style={{ marginBottom: 4 }}>
                                <SubHeader icon={FaUsers} iconColor={T.purple} label={t('app.trends.key_voices')} count={keyVoices.length} collapsedKey="keyVoices" />
                                {!collapsed.keyVoices && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                                        {keyVoices.slice(0, 7).map((inf, idx) => {
                                            const platform = (inf.platform || 'twitter').toLowerCase();
                                            const PlatformIcon = PLATFORM_ICONS[platform] || FaGlobe;
                                            const platformColor = PLATFORM_COLORS[platform] || '#8e8e93';
                                            const stanceColor = STANCE_COLORS[(inf.stance || 'neutral').toLowerCase()] || T.textMuted;
                                            const avatarColor = AVATAR_PALETTE[idx % AVATAR_PALETTE.length];
                                            const initial = (inf.name || '?')[0].toUpperCase();
                                            // Support both key_influencer shape (bio/reach) and key_voice shape (description)
                                            const bodyText = inf.description || inf.bio;
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => inf.post_url ? window.open(inf.post_url, '_blank') : handleInfluencerPress(inf)}
                                                    style={{
                                                        display: 'flex', gap: 12, alignItems: 'flex-start',
                                                        padding: '12px 14px', borderRadius: 10,
                                                        background: T.card, border: `1px solid ${T.border}`,
                                                        cursor: 'pointer', transition: 'background 0.12s',
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 38, height: 38, borderRadius: 19,
                                                        background: '#e5e7eb', flexShrink: 0,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 700 }}>
                                                            {initial}
                                                        </span>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center',
                                                            gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                                                            <span style={{ color: T.text, fontSize: 13,
                                                                fontWeight: 700, lineHeight: '16px' }}>
                                                                {inf.name}
                                                            </span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                <PlatformIcon size={10} color={platformColor} />
                                                                <span style={{ color: platformColor, fontSize: 9,
                                                                    fontWeight: 700, textTransform: 'uppercase',
                                                                    letterSpacing: 0.4 }}>
                                                                    {platform}
                                                                </span>
                                                            </div>
                                                            {inf.stance && (
                                                                <span style={{
                                                                    fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                                                                    textTransform: 'uppercase', color: stanceColor,
                                                                    background: `${stanceColor}18`,
                                                                    padding: '1px 6px', borderRadius: 4,
                                                                }}>
                                                                    {inf.stance.toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {bodyText && (
                                                            <div style={{
                                                                color: T.textSec, fontSize: 12, lineHeight: '17px',
                                                                overflow: 'hidden', display: '-webkit-box',
                                                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                                                marginBottom: 5,
                                                            }}>
                                                                {bodyText}
                                                            </div>
                                                        )}
                                                        {inf.reach && (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <FaUsers size={9} color={T.textMuted} />
                                                                <span style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>
                                                                    {inf.reach}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {inf.post_url && (
                                                            <div
                                                                onClick={(e) => { e.stopPropagation(); window.open(inf.post_url, '_blank'); }}
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 5, cursor: 'pointer' }}
                                                            >
                                                                <FaExternalLinkAlt size={9} color={T.primary} />
                                                                <span style={{ color: T.primary, fontSize: 11, fontWeight: 500 }}>{t('app.trends.view_post')}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Emerging Trends ── */}
                        {emerging && (
                            <div style={{ marginBottom: 4 }}>
                                <SubHeader icon={FaBullhorn} iconColor={T.success} label={t('app.trends.emerging_trends')} collapsedKey="emerging" />
                                {!collapsed.emerging && (
                                    <div style={{ marginBottom: 16 }}>
                                        <MarkdownText content={emerging} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSignalsSection = () => (
        <div style={{ marginBottom: 8 }}>
            <SectionHeader
                icon={FaBrain}
                iconColor={T.success}
                title={t('app.trends.audience_signals')}
                isCollapsed={collapsed.signals}
                onToggle={() => toggleSection('signals')}
                extra={<InfoTooltip text={enrichmentData.psychology_reasoning || enrichmentData.psychology?.reasoning || "Sentiment analysis."} title="PSYCHOLOGY ANALYSIS" />}
            />
            {!collapsed.signals && (
                <div style={{ marginBottom: 20, padding: '4px 0' }}>
                    {/* Sentiment */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
                                {t('app.trends.sentiment')}
                            </span>
                            <span style={{
                                color: (enrichmentData.sentiment_score || 0) > 0 ? T.success : T.error,
                                fontSize: 16, fontWeight: 700, fontFamily: 'monospace',
                            }}>
                                {((enrichmentData.sentiment_score || 0) * 100).toFixed(0)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                            marginBottom: 4, paddingInline: 2 }}>
                            {[
                                { key: 'negative', label: t('app.trends.negative') },
                                { key: 'neutral', label: t('app.trends.neutral') },
                                { key: 'positive', label: t('app.trends.positive') },
                            ].map(l => (
                                <span key={l.key} style={{ color: T.textMuted, fontSize: 8,
                                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {l.label}
                                </span>
                            ))}
                        </div>
                        <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', borderRadius: 3,
                                width: `${((enrichmentData.sentiment_score || 0) + 1) * 50}%`,
                                background: T.primary,
                            }} />
                        </div>
                    </div>

                    {/* Controversy */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
                                    {t('app.trends.controversy')}
                                </span>
                                <InfoTooltip text="Measures the divergence in conversation." />
                            </div>
                            <span style={{ color: T.text, fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>
                                {((enrichmentData.controversy_index || 0) * 100).toFixed(0)}%
                            </span>
                        </div>
                        <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', borderRadius: 3,
                                width: `${(enrichmentData.controversy_index || 0) * 100}%`,
                                background: T.primary,
                            }} />
                        </div>
                    </div>

                    {/* Dominant Emotion */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
                            {t('app.trends.dominant_emotion')}
                        </span>
                        <span style={{
                            color: T.text, fontSize: 13, fontWeight: 600,
                            background: T.surface, padding: '2px 8px',
                            borderRadius: 4,
                        }}>
                            {enrichmentData.dominant_emotion?.toUpperCase()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    const renderSourcesSection = () => (
        <div style={{ marginBottom: 8 }}>
            <SectionHeader
                icon={FaBullhorn}
                iconColor={T.warning}
                title={t('app.trends.sources')}
                isCollapsed={collapsed.sources}
                onToggle={() => toggleSection('sources')}
                extra={<InfoTooltip text={enrichmentData.source_reasoning || enrichmentData.source_analysis?.reasoning || "Source reliability assessment."} title="SOURCE ANALYSIS" />}
            />
            {!collapsed.sources && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 16 }}>
                        <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
                            {t('app.trends.credibility_score')}
                        </span>
                        <span style={{ color: T.text, fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>
                            {((enrichmentData.credibility_score || 0) * 100).toFixed(0)}%
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                            { key: 'twitter',   label: t('app.trends.platform_twitter'),   color: '#1d9bf0' },
                            { key: 'telegram',  label: t('app.trends.platform_telegram'),  color: '#24A1DE' },
                            { key: 'instagram', label: t('app.trends.platform_instagram'), color: '#e1306c' },
                            { key: 'tiktok',    label: t('app.trends.platform_tiktok'),    color: '#ff0050' },
                            { key: 'rss',       label: t('app.trends.platform_rss'),       color: '#f97316' },
                        ].filter(s => (enrichmentData.source_breakdown?.[s.key] || 0) > 0).map(s => (
                            <div key={s.key}>
                                <div style={{ display: 'flex', justifyContent: 'space-between',
                                    marginBottom: 3 }}>
                                    <span style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
                                        letterSpacing: 0.5 }}>
                                        {s.label}
                                    </span>
                                    <span style={{ color: T.textMuted, fontSize: 9 }}>
                                        {enrichmentData.source_breakdown[s.key]}%
                                    </span>
                                </div>
                                <div style={{ height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', borderRadius: 2,
                                        width: `${enrichmentData.source_breakdown[s.key]}%`,
                                        background: T.primary,
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderEntitiesSection = () => {
        const ents = enrichmentData?.entities || {};
        const people = ents.people || [];
        const orgs = ents.organizations || [];
        const locs = ents.locations || [];
        if (!people.length && !orgs.length && !locs.length) return null;

        const groups = [
            { label: 'PEOPLE', items: people, color: T.primary },
            { label: 'ORGANIZATIONS', items: orgs, color: T.warning },
            { label: 'LOCATIONS', items: locs, color: T.success },
        ].filter(g => g.items.length > 0);

        return (
            <div style={{ marginBottom: 8 }}>
                <SectionHeader
                    icon={FaUserSecret}
                    iconColor={T.purple}
                    title={t('app.trends.key_entities')}
                    isCollapsed={collapsed.entities}
                    onToggle={() => toggleSection('entities')}
                    extra={enrichmentData.entities_reasoning
                        ? <InfoTooltip text={enrichmentData.entities_reasoning} />
                        : null}
                />
                {!collapsed.entities && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
                        {groups.map(g => (
                            <div key={g.label}>
                                <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
                                    letterSpacing: 0.8, marginBottom: 8 }}>
                                    {g.label}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {g.items.slice(0, 10).map((item, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                background: T.surface,
                                                color: T.textSec,
                                                border: `1px solid ${T.border}`,
                                                padding: '4px 9px', borderRadius: 12,
                                                fontSize: 11, fontWeight: 500,
                                            }}
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderRightPanel = () => {
        if (!selectedNarrative) {
            return (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 16, background: T.bg }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: 20,
                        background: 'rgba(10,132,255,0.08)',
                        border: '1px solid rgba(10,132,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FaChartLine size={28} color={T.textMuted} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                            {t('app.trends.select_narrative')}
                        </div>
                        <div style={{ color: T.textMuted, fontSize: 13 }}>
                            {t('app.trends.select_narrative_hint')}
                        </div>
                    </div>
                </div>
            );
        }

        const isSurge = (selectedNarrative.velocity_1h || 0) >= 5;

        const buildPostUrl = (item) => {
            if (item.url) return item.url;
            if (item.source === 'twitter' && item.message_id && item.username)
                return `https://twitter.com/${item.username}/status/${item.message_id}`;
            if (item.source === 'tiktok' && item.username && item.message_id)
                return `https://www.tiktok.com/@${item.username}/video/${item.message_id}`;
            if (item.source === 'instagram' && item.username)
                return `https://www.instagram.com/${item.username}/`;
            return null;
        };

        const formatStat = (n) => {
            if (!n && n !== 0) return null;
            if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
            if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
            return String(n);
        };

        const PostTile = ({ item }) => {
            const url = buildPostUrl(item);
            const platform = item.source || 'twitter';
            const PlatformIcon = PLATFORM_ICONS[platform] || FaGlobe;
            const platformColor = PLATFORM_COLORS[platform] || '#8e8e93';
            const handle = item.username || item.author_name || '';
            const shortHandle = handle.length > 12 ? handle.slice(0, 12) + '…' : handle;
            const dateStr = item.created_at
                ? new Date(item.created_at).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
                : '';
            const images = item.tweet_images || [];
            const hasImage = images.length > 0;
            const views = formatStat(item.view_count);
            const likes = formatStat(item.like_count || item.likes);
            const comments = formatStat(item.reply_count || item.comments);
            const content = item.translated_content || item.content || item.tweet_content || '';
            // vary text-tile heights naturally by clamping to different line counts based on content length
            const textClamp = content.length > 180 ? 8 : content.length > 80 ? 5 : 3;

            return (
                <div
                    className="nm-post-tile"
                    onClick={() => url && window.open(url, '_blank')}
                    style={{
                        borderRadius: 12, overflow: 'hidden',
                        background: T.card,
                        border: `1px solid ${T.border}`,
                        cursor: url ? 'pointer' : 'default',
                        marginBottom: 8,
                        breakInside: 'avoid',
                    }}
                >
                    {hasImage && (
                        <img
                            src={images[0]}
                            alt=""
                            style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    {content && (
                        <div style={{
                            padding: '12px 12px 8px',
                            color: T.text, fontSize: 12.5, lineHeight: '18px',
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: textClamp, WebkitBoxOrient: 'vertical',
                        }}>
                            {content}
                        </div>
                    )}
                    <div style={{ padding: '7px 10px 9px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                            <PlatformIcon size={9} color={platformColor} />
                            <span style={{ color: T.textSec, fontSize: 11, fontWeight: 600, flex: 1,
                                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                @{shortHandle}
                            </span>
                            <span style={{ color: T.textMuted, fontSize: 10, flexShrink: 0 }}>{dateStr}</span>
                        </div>
                        {(views || likes || comments) && (
                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                                {views && <span style={{ color: T.textMuted, fontSize: 10 }}>👁 {views}</span>}
                                {likes && <span style={{ color: T.textMuted, fontSize: 10 }}>♡ {likes}</span>}
                                {comments && <span style={{ color: T.textMuted, fontSize: 10 }}>💬 {comments}</span>}
                            </div>
                        )}
                    </div>
                </div>
            );
        };

        const postsList = (
            <div>
                {loadingMessages ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                        <ActivityIndicator size="small" color={T.primary} />
                    </div>
                ) : narrativeMessages.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: 8, paddingTop: 20 }}>
                        <FaCommentAlt size={20} color={T.textMuted} />
                        <span style={{ color: T.textMuted, fontSize: 13 }}>{t('app.trends.no_posts_yet')}</span>
                    </div>
                ) : (
                    <div style={{ columnCount: isMobile ? 1 : 3, columnGap: 8 }}>
                        {narrativeMessages.map(item => (
                            <PostTile key={item.message_id || Math.random()} item={item} />
                        ))}
                    </div>
                )}
            </div>
        );

        const rightSidePanel = !isMobile && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                borderLeft: `1px solid ${T.border}` }}>
                {/* Right column header */}
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`,
                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('app.trends.signals_and_posts')}
                    </span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px',
                    display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Signals / Sources / Entities — moved here */}
                    {!loadingEnrichment && enrichmentData && (
                        <>
                            {renderSignalsSection()}
                            {renderSourcesSection()}
                            {renderEntitiesSection()}
                        </>
                    )}
                    {loadingEnrichment && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '20px 0' }}>
                            <ActivityIndicator size="small" color={T.primary} />
                            <span style={{ color: T.primary, fontSize: 10, letterSpacing: 2, fontWeight: 600 }}>
                                {t('app.trends.loading')}
                            </span>
                        </div>
                    )}
                    {!enrichmentRequested && !enrichmentData && !loadingEnrichment && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0', gap: 10 }}>
                            <span style={{ color: T.textMuted, fontSize: 11 }}>{t('app.trends.enrichmentHint')}</span>
                        </div>
                    )}
                    {/* Recent Posts header */}
                    <div style={{ borderTop: enrichmentData ? `1px solid ${T.border}` : 'none',
                        paddingTop: enrichmentData ? 16 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                            <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('app.trends.recent_posts')}
                            </span>
                            {narrativeMessages.length > 0 && (
                                <span style={{ color: T.primary, fontSize: 11, fontWeight: 700 }}>
                                    ({narrativeMessages.length})
                                </span>
                            )}
                        </div>
                        {postsList}
                    </div>
                </div>
            </div>
        );

        return (
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <div style={{ width: isMobile ? '100%' : midW, flexShrink: 0, overflowY: 'auto', padding: '24px 28px',
                display: 'flex', flexDirection: 'column', flex: isMobile ? 1 : undefined }}>
                {/* Detail header */}
                <div style={{
                    paddingBottom: 24, marginBottom: 24,
                    borderBottom: `1px solid ${T.border}`,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{
                            fontSize: 11, fontWeight: 500,
                            padding: '3px 10px', borderRadius: 20,
                            border: `1px solid ${T.border}`,
                            background: T.surface, color: T.textSec,
                        }}>
                            {formatVelocity(selectedNarrative.velocity_1h)}
                        </span>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>
                                {formatTime(selectedNarrative.last_seen_at)}
                            </span>
                            <div
                                onClick={(e) => { e.stopPropagation(); setShareNarrative(selectedNarrative); }}
                                style={{
                                    width: 28, height: 28, borderRadius: 8,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(80,89,201,0.1)',
                                    border: '1px solid rgba(80,89,201,0.25)',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaShareAlt size={10} color="#5059C9" />
                            </div>
                        </div>
                    </div>
                    <div style={{ color: T.text, fontSize: 20, fontWeight: 700,
                        lineHeight: '26px', marginBottom: 8, letterSpacing: -0.3 }}>
                        {selectedNarrative.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FaCommentAlt size={10} color={T.textMuted} />
                        <span style={{ color: T.textMuted, fontSize: 11 }}>
                            {t('app.trends.postCount', { count: narrativeMessages.length > 0 ? narrativeMessages.length : selectedNarrative.message_count })}
                        </span>
                    </div>
                    {selectedNarrative.summary && (
                        <div style={{ color: T.textSec, fontSize: 14, lineHeight: '21px',
                            marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                            {selectedNarrative.summary}
                        </div>
                    )}
                </div>


                {/* Advisor section — always visible */}
                {renderStrategySection()}

                {/* Enrichment: on-demand button */}
                        {!enrichmentRequested && !enrichmentData && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                                <button
                                    onClick={handleRequestEnrichment}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '12px 24px', borderRadius: 8,
                                        background: T.primary,
                                        border: 'none', cursor: 'pointer',
                                        color: '#fff', fontSize: 13, fontWeight: 700,
                                    }}
                                >
                                    <FaSearch style={{ fontSize: 12 }} />
                                    {t('app.trends.getAiAnalysis')}
                                </button>
                            </div>
                        )}

                        {/* Enrichment loading */}
                        {loadingEnrichment && (
                            <div style={{ display: 'flex', flexDirection: 'column',
                                alignItems: 'center', padding: '32px 0', gap: 10 }}>
                                <ActivityIndicator size="small" color={T.primary} />
                                <span style={{ color: T.primary, fontSize: 10,
                                    letterSpacing: 2, fontWeight: 600 }}>
                                    {t('app.trends.loading_analysis')}
                                </span>
                            </div>
                        )}

                        {/* Enrichment sections — signals/sources/entities moved to right panel on desktop */}
                        {!loadingEnrichment && enrichmentData && (
                            <div style={{ marginBottom: 16 }}>
                                {renderAnalysisSection()}
                                {isMobile && renderSignalsSection()}
                                {isMobile && renderSourcesSection()}
                                {isMobile && renderEntitiesSection()}
                            </div>
                        )}

                        {/* Mobile: show posts inline below analysis */}
                        {isMobile && (
                            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 20 }}>
                                <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                                    {t('app.trends.recent_posts')}
                                    {narrativeMessages.length > 0 && (
                                        <span style={{ color: T.primary, marginLeft: 6 }}>
                                            ({narrativeMessages.length})
                                        </span>
                                    )}
                                </div>
                                {postsList}
                            </div>
                        )}

                <div style={{ height: 60 }} />
            </div>
            {/* Desktop-only: right posts panel */}
            {!isMobile && <div className="nm-divider" onMouseDown={(e) => startDrag('right', e)} />}
            {rightSidePanel}
            </div>
        );
    };

    // ── Sidebar JSX ──────────────────────────────────────────────────────────

    const sidebarJsx = (
        <div style={{
            width: isMobile ? '100%' : sidebarW,
            flexShrink: 0,
            borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            height: '100%',
        }}>
            {/* Sidebar header */}
            <div style={{
                padding: '16px 16px 12px',
                borderBottom: `1px solid ${T.border}`,
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                        <div style={{ color: T.text, fontSize: 17, fontWeight: 700,
                            letterSpacing: 0.3, marginBottom: 2 }}>
                            {t('app.trends.narrative_monitor')}
                        </div>
                        <div style={{ color: T.textMuted, fontSize: 10, letterSpacing: 1.2,
                            fontWeight: 600 }}>
                            {t('app.trends.real_time_tracking')}
                        </div>
                    </div>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        background: 'rgba(48,209,88,0.1)',
                        padding: '4px 10px', borderRadius: 20,
                        border: '1px solid rgba(48,209,88,0.2)',
                    }}>
                        <div style={{ width: 6, height: 6, borderRadius: 3,
                            background: '#30d158' }} />
                        <span style={{ color: '#30d158', fontSize: 10, fontWeight: 700 }}>
                            LIVE
                        </span>
                    </div>
                </div>

                {/* View mode toggle */}
                <div style={{ display: 'flex', background: T.surface,
                    borderRadius: 8, padding: 3 }}>
                    {[
                        { key: 'trends', label: t('app.trends.view_trends') },
                        { key: 'strategies', label: t('app.trends.view_my_strategies') },
                    ].map(v => (
                        <div
                            key={v.key}
                            className="nm-toggle"
                            onClick={() => setViewMode(v.key)}
                            style={{
                                flex: 1, textAlign: 'center',
                                padding: '6px 0', borderRadius: 6,
                                background: viewMode === v.key ? T.card : 'transparent',
                                cursor: 'pointer',
                                transition: 'background 0.12s',
                            }}
                        >
                            <span style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                color: viewMode === v.key ? T.text : T.textMuted,
                            }}>
                                {v.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Narrative count */}
            {viewMode === 'trends' && (
                <div style={{ padding: '8px 16px 4px', flexShrink: 0 }}>
                    <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>
                        {t('app.trends.active_narratives_count', { count: activeNarratives.length })}
                    </span>
                </div>
            )}

            {/* Scrollable list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 8px' }}>
                {renderSidebarList()}
            </div>
        </div>
    );

    // ── Mobile detail view ───────────────────────────────────────────────────

    const detailViewJsx = (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {isMobile && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                    flexShrink: 0,
                }}>
                    <div
                        onClick={() => setShowDetail(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6,
                            cursor: 'pointer', color: T.primary, fontSize: 13, fontWeight: 600 }}
                    >
                        <FaArrowLeft size={12} color={T.primary} />
                        {t('app.trends.back')}
                    </div>
                </div>
            )}
            {renderRightPanel()}
        </div>
    );

    // ── Main render ──────────────────────────────────────────────────────────

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />

            <div style={{
                display: 'flex', flexDirection: 'column',
                height: '100vh', background: T.bg, overflow: 'hidden',
            }}>
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {isMobile ? (
                        showDetail ? detailViewJsx : sidebarJsx
                    ) : (
                        <>
                            {sidebarJsx}
                            <div className="nm-divider" onMouseDown={(e) => startDrag('left', e)} />
                            {detailViewJsx}
                        </>
                    )}
                </div>
            </div>

            {/* Influencer Details Modal */}
            <Modal
                isVisible={showInfluencerModal}
                onClose={() => setShowInfluencerModal(false)}
                title={selectedInfluencer?.name || t('app.trends.entity_details')}
            >
                <div style={{ padding: 24 }}>
                    {[
                        { label: t('app.trends.influencer_platform'), value: selectedInfluencer?.platform?.toUpperCase() },
                        { label: t('app.trends.influencer_reach'), value: selectedInfluencer?.reach },
                        {
                            label: t('app.trends.influencer_stance'), value: selectedInfluencer?.stance?.toUpperCase(),
                            color: selectedInfluencer?.stance === 'hostile' ? T.error
                                : selectedInfluencer?.stance === 'friendly' ? T.success : T.textMuted,
                        },
                    ].map(row => (
                        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 12 }}>
                            <span style={{ color: T.textSec, fontSize: 12 }}>{row.label}</span>
                            <span style={{ color: row.color || T.text, fontSize: 13, fontWeight: 600 }}>
                                {row.value}
                            </span>
                        </div>
                    ))}
                    {selectedInfluencer?.bio && (
                        <div style={{ marginTop: 16, paddingTop: 16,
                            borderTop: `1px solid ${T.border}` }}>
                            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700,
                                letterSpacing: 1, marginBottom: 8 }}>
                                {t('app.trends.bio_description')}
                            </div>
                            <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px' }}>
                                {selectedInfluencer.bio}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Counter-Narrative Modal */}
            <CounterNarrativeModal
                isVisible={showReactionModal}
                onClose={() => setShowReactionModal(false)}
                counterNarrativeData={counterNarrative}
                isLoading={reactionLoading}
                onRegenerate={() => handleReactToNarrative(reactionNarrative, enrichmentData, null)}
                platform="twitter"
                error={reactionError}
            />

            {/* Share Modal */}
            {shareNarrative && (
                <ShareModal
                    type="narrative"
                    id={shareNarrative.id}
                    title={shareNarrative.title}
                    summary={shareNarrative.summary}
                    onClose={() => setShareNarrative(null)}
                />
            )}
        </>
    );
};

export default TrendsScreen;
